import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router';
import { useEffect, useState, useRef } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  Home, Building2, FlaskConical, Rocket, Shield,
  Send, Globe, Users, Mail, Trophy, BookOpen, LogOut, User, ShieldAlert, Bell, MessageSquare, X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ResourceBar } from './ResourceBar';
import { BottomNav } from './BottomNav';
import { usePlanetContext } from '../context/PlanetContext';
import { useNotifications } from '../context/NotificationsContext';
import { cn } from '../lib/utils';

const NAV_GROUPS = [
  [
    { to: '/',          icon: Home,         label: 'Kommando'      },
  ],
  [
    { to: '/buildings', icon: Building2,    label: 'Infrastruktur' },
    { to: '/research',  icon: FlaskConical, label: 'Technologie'   },
  ],
  [
    { to: '/shipyard',  icon: Rocket,       label: 'Werft'         },
    { to: '/defense',   icon: Shield,       label: 'Abwehr'        },
    { to: '/fleet',     icon: Send,         label: 'Flotte'        },
  ],
  [
    { to: '/galaxy',    icon: Globe,        label: 'Sektor'        },
    { to: '/alliance',  icon: Users,        label: 'Koalition'     },
  ],
  [
    { to: '/messages',  icon: Mail,         label: 'Transmissionen'},
    { to: '/chat',      icon: MessageSquare,label: 'Chat'          },
    { to: '/highscore', icon: Trophy,       label: 'Rangliste'     },
    { to: '/help',      icon: BookOpen,     label: 'Codex'         },
  ],
  [
    { to: '/bruecke',   icon: User,         label: 'Brücke'        },
    { to: '/admin',     icon: ShieldAlert,  label: 'Admin'         },
  ],
];

// ── Dot grid decoration ───────────────────────────────────────────────────────
function Dots({ rows, cols, opacity = 35 }: { rows: number; cols: number; opacity?: number }) {
  return (
    <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${cols}, 4px)` }}>
      {Array.from({ length: rows * cols }).map((_, i) => (
        <span key={i} className={`w-1 h-1 bg-crystal-400/${opacity}`} />
      ))}
    </div>
  )
}


function PlanetSelector() {
  const { planets, selectedPlanetId, setSelectedPlanetId } = usePlanetContext()
  const selected = planets.find(p => p.id === selectedPlanetId) ?? planets[0]
  if (!selected) return null

  const coords = `[${selected.galaxy}:${selected.system}:${selected.position}]`

  // Single planet: just show name + coords, no dropdown
  if (planets.length <= 1) {
    return (
      <div className="hidden sm:flex flex-col items-end leading-none">
        <span className="font-mono text-xs text-slate-200">{selected.name}</span>
        <span className="font-mono text-[10px] text-crystal-400/70">{coords}</span>
      </div>
    )
  }

  // Multiple planets: dropdown with name + coords visible
  return (
    <div className="flex flex-col items-end leading-none gap-0.5">
      <span className="font-mono text-[9px] text-slate-500 tracking-widest uppercase hidden sm:inline">Planet</span>
      <select
        value={selectedPlanetId ?? ''}
        onChange={e => setSelectedPlanetId(e.target.value)}
        className="bg-space-950 border border-nova-500/40 text-nova-300 text-xs font-mono px-2 py-1 focus:outline-none focus:border-nova-400/60 cursor-pointer transition-colors hover:border-nova-400/60"
      >
        {planets.map(p => (
          <option key={p.id} value={p.id}>
            {p.is_homeworld ? '⌂' : '◉'} {p.name} [{p.galaxy}:{p.system}:{p.position}]
          </option>
        ))}
      </select>
    </div>
  )
}

interface Props { session: Session }

function formatTimeUntil(iso: string): string {
  const s = Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${sec}s`
  return `${sec}s`
}

export function GameLayout({ session: _session }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const notifRef = useRef<HTMLDivElement>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [protectionUntil, setProtectionUntil] = useState<string | null>(null);
  const [remainingTicks, setRemainingTicks] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const { count: notifCount, urgent, unread, chatUnread, attacks, transients, dismiss, resetUnread, resetChatUnread } = useNotifications();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) return;
      supabase.from('profiles').select('is_admin, protection_until').eq('id', data.user.id).maybeSingle()
        .then(({ data: p }) => {
          setIsAdmin(p?.is_admin ?? false);
          setProtectionUntil(p?.protection_until ?? null);
        });
    });
  }, []);

  useEffect(() => {
    function calc() {
      if (!protectionUntil) { setRemainingTicks(0); return; }
      const ms = new Date(protectionUntil).getTime() - Date.now();
      setRemainingTicks(Math.max(0, Math.floor(ms / (300 * 1000))));
    }
    calc();
    const id = setInterval(calc, 60_000);
    return () => clearInterval(id);
  }, [protectionUntil]);

  // Spielzeit-Tracking: Startzeit merken + jede Minute +60s in DB
  useEffect(() => {
    if (!sessionStorage.getItem('session_start')) {
      sessionStorage.setItem('session_start', String(Date.now()));
    }
    const id = setInterval(() => {
      supabase.rpc('increment_play_time', { seconds_to_add: 60 });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Close dropdown + reset badge counts when navigating to the relevant page
  useEffect(() => {
    setNotifOpen(false);
    if (location.pathname === '/messages') resetUnread();
    if (location.pathname === '/chat')     resetChatUnread();
  }, [location.pathname]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!notifOpen) return;
    function handler(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  return (
    <div className="flex flex-col min-h-dvh bg-space-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-50 bg-space-950 border-b border-crystal-400/25"
        style={{ boxShadow: '0 4px 20px rgba(103,232,249,0.07)' }}
      >
        {/* Header: Logo (fixed left) | Resource-Bar (scrollable) | Bell (fixed right) */}
        <div className="flex items-center gap-2 px-3 md:px-4 py-2">

          {/* Left: Logo — always visible, never pushed off screen */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <img
              src="/Logo_Nova_Sigma-nobg.png"
              alt="Nova Sigma"
              className="h-8 md:h-9 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(59,130,246,0.45))' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                document.getElementById('logo-fallback')?.style.setProperty('display', 'flex');
              }}
            />
            <span id="logo-fallback" className="hidden items-center gap-2 font-mono font-bold text-crystal-400 text-lg tracking-widest">
              NOVA SIGMA
            </span>
            <span className="hidden sm:inline font-mono text-[8px] tracking-[0.25em] uppercase px-1.5 py-0.5 border border-orange-500/50 text-orange-400 bg-orange-500/10">
              Alpha
            </span>
          </div>

          {/* Middle: Resource-Bar scrollt horizontal auf Mobile (CLAUDE.md design intent) */}
          <div className="flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="flex items-center justify-end gap-2 md:gap-3 min-w-max">
              {/* Newbie protection — desktop only */}
              {remainingTicks > 0 && (
                <div
                  className="hidden sm:flex items-center gap-1.5 px-2 py-1 border border-green-500/40 bg-green-500/10 flex-shrink-0"
                  title={`Sigma-Direktionsschutz aktiv — noch ${remainingTicks} Ticks`}
                >
                  <Shield className="w-3.5 h-3.5 text-green-400" style={{ filter: 'drop-shadow(0 0 4px rgba(74,222,128,0.6))' }} />
                  <span className="font-mono text-[10px] text-green-400">{remainingTicks} Ticks</span>
                </div>
              )}
              <ResourceBar />
              <PlanetSelector />
            </div>
          </div>

          {/* ── Notification bell with dropdown ───────────────────────────── */}
          <div className="relative flex-shrink-0" ref={notifRef}>
            <button
              onClick={() => setNotifOpen(v => !v)}
              title={notifCount > 0 ? `${notifCount} Meldung${notifCount > 1 ? 'en' : ''}` : 'Keine Meldungen'}
              className="relative flex items-center justify-center w-8 h-8"
            >
              <Bell
                className={cn(
                  'w-4 h-4 transition-colors',
                  notifCount === 0 && 'text-slate-400',
                  notifCount > 0 && !urgent && 'text-amber-400 animate-pulse',
                  urgent && 'text-red-400 animate-pulse',
                )}
                style={notifCount > 0 ? {
                  filter: urgent
                    ? 'drop-shadow(0 0 6px rgba(239,68,68,0.8))'
                    : 'drop-shadow(0 0 6px rgba(251,191,36,0.7))',
                } : undefined}
              />
              {notifCount > 0 && (
                <span className={cn(
                  'absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] rounded-full flex items-center justify-center font-mono text-[9px] font-bold text-white',
                  urgent ? 'bg-red-500' : 'bg-amber-500',
                )}>
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-space-950 border border-crystal-400/30 z-50 shadow-2xl"
                style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(103,232,249,0.1)' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-crystal-400/15">
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-crystal-400/60">Meldungen</span>
                  <button onClick={() => setNotifOpen(false)} className="text-slate-500 hover:text-slate-300 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* Attacks */}
                  {attacks.map(f => (
                    <button
                      key={f.id}
                      onClick={() => { navigate('/'); setNotifOpen(false); }}
                      className="w-full flex items-start gap-3 px-3 py-2.5 border-b border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-colors text-left"
                    >
                      <span className="text-red-400 mt-0.5 flex-shrink-0">⚠</span>
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-red-300 leading-snug">
                          <span className="font-bold">{f.attacker}</span> greift <span className="text-red-400">{f.planet_name}</span> an
                        </p>
                        <p className="font-mono text-[10px] text-red-400/60 mt-0.5">
                          Ankunft in {formatTimeUntil(f.arrives_at)}
                        </p>
                      </div>
                    </button>
                  ))}

                  {/* Unread transmissions */}
                  {unread > 0 && (
                    <Link
                      to="/messages"
                      onClick={() => setNotifOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 border-b border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-xs text-amber-300">
                          {unread} ungelesene Transmission{unread > 1 ? 'en' : ''}
                        </p>
                      </div>
                      <span className="font-mono text-[9px] bg-amber-500 text-white px-1.5 py-0.5 flex-shrink-0">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    </Link>
                  )}

                  {/* Unread chat */}
                  {chatUnread > 0 && (
                    <Link
                      to="/chat"
                      onClick={() => setNotifOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 border-b border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-xs text-amber-300">
                          {chatUnread} ungelesene Chat-Nachricht{chatUnread > 1 ? 'en' : ''}
                        </p>
                      </div>
                      <span className="font-mono text-[9px] bg-amber-500 text-white px-1.5 py-0.5 flex-shrink-0">
                        {chatUnread > 9 ? '9+' : chatUnread}
                      </span>
                    </Link>
                  )}

                  {/* Transients */}
                  {transients.map(t => (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 px-3 py-2.5 border-b border-crystal-400/10 bg-crystal-400/5"
                    >
                      <span className="text-crystal-400/70 flex-shrink-0 text-xs">◈</span>
                      <p className="font-mono text-xs text-crystal-300 flex-1 min-w-0 leading-snug">{t.label}</p>
                      <button
                        onClick={() => dismiss(t.id)}
                        className="text-slate-500 hover:text-slate-300 flex-shrink-0 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {/* Empty state */}
                  {notifCount === 0 && (
                    <div className="px-3 py-6 flex items-center justify-center">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Keine Meldungen</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">

        {/* ── Sidebar ──────────────────────────────────────────────────────── */}
        <nav
          className="hidden md:flex flex-col w-48 shrink-0 relative bg-space-950 border-r border-crystal-400/30"
          style={{ boxShadow: '4px 0 40px rgba(103,232,249,0.07), 8px 0 64px rgba(103,232,249,0.03)' }}
        >
          {/* Outer corner brackets — large + bright */}
          <span className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-crystal-400 z-10 pointer-events-none" />
          <span className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-crystal-400 z-10 pointer-events-none" />
          <span className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-crystal-400 z-10 pointer-events-none" />
          <span className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-crystal-400 z-10 pointer-events-none" />
          {/* Left accent bar */}
          <div className="absolute left-0 top-8 bottom-8 w-0.5 bg-crystal-400/40 z-10 pointer-events-none" />
          {/* Right inner accent line */}
          <div className="absolute right-0 top-8 bottom-8 w-px bg-crystal-400/15 z-10 pointer-events-none" />

          {/* Top section */}
          <div className="px-4 py-3 border-b border-crystal-400/15">
            <div className="flex justify-between items-center mb-2">
              <Dots rows={3} cols={4} opacity={45} />
              <div className="flex flex-col gap-1">
                <div className="w-12 h-px bg-crystal-400/30" />
                <div className="w-8 h-px bg-crystal-400/20" />
                <div className="w-10 h-px bg-crystal-400/15" />
              </div>
              <Dots rows={3} cols={4} opacity={45} />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-crystal-400/80 animate-pulse" style={{ boxShadow: '0 0 6px rgba(103,232,249,0.8)' }} />
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-crystal-400/50">Nav Sys</span>
              <div className="flex-1 h-px bg-crystal-400/15" />
            </div>
          </div>

          {/* Nav groups */}
          <ul className="flex-1 px-2 py-2 overflow-y-auto">
            {NAV_GROUPS.map((group, gIdx) => (
              <li key={gIdx}>
                {/* Group separator (before every group except first) */}
                {gIdx > 0 && (
                  <div className="flex items-center gap-2 px-1 my-1.5">
                    <div className="w-1 h-px bg-crystal-400/40" />
                    <div className="flex-1 h-px bg-crystal-400/15" />
                    <Dots rows={1} cols={2} opacity={25} />
                  </div>
                )}
                <ul className="space-y-0.5">
                  {group.filter(({ to }) => to !== '/admin' || isAdmin).map(({ to, icon: Icon, label }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        end={to === '/'}
                        className={({ isActive }) =>
                          `relative flex items-center gap-2.5 px-3 py-2 text-xs font-mono tracking-wider uppercase transition-all duration-150 ${
                            isActive
                              ? 'text-nova-300 border border-nova-400/60 bg-nova-600/15'
                              : 'text-slate-400 hover:text-crystal-400 border border-transparent hover:border-crystal-400/25 hover:bg-crystal-400/5'
                          }`
                        }
                        style={({ isActive }) => isActive
                          ? { boxShadow: '0 0 14px rgba(96,165,250,0.30), inset 0 0 20px rgba(96,165,250,0.07)' }
                          : undefined
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {/* HUD frame on active item — larger brackets */}
                            {isActive && <>
                              <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-nova-400" />
                              <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-nova-400" />
                              <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-nova-400" />
                              <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-nova-400" />
                              {/* Left accent bar */}
                              <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-nova-400" style={{ boxShadow: '0 0 6px rgba(96,165,250,0.8)' }} />
                              {/* Top dash line */}
                              <span className="absolute top-1.5 left-5 w-4 h-px bg-nova-400/50" />
                            </>}
                            <Icon className="w-3.5 h-3.5 shrink-0" />
                            {label}
                            {to === '/messages' && unread > 0 && (
                              <span className="ml-auto font-mono text-[9px] bg-amber-500 text-white leading-none px-1 py-0.5 min-w-[16px] text-center">
                                {unread > 9 ? '9+' : unread}
                              </span>
                            )}
                            {to === '/chat' && chatUnread > 0 && (
                              <span className="ml-auto font-mono text-[9px] bg-amber-500 text-white leading-none px-1 py-0.5 min-w-[16px] text-center">
                                {chatUnread > 9 ? '9+' : chatUnread}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {/* Bottom: dot decoration + logout */}
          <div className="border-t border-crystal-400/15 px-4 pt-3 pb-4">
            <div className="flex justify-between items-end mb-3">
              <Dots rows={3} cols={3} opacity={30} />
              <div className="flex flex-col gap-1 items-end">
                <div className="w-8 h-px bg-crystal-400/20" />
                <div className="w-12 h-px bg-crystal-400/15" />
              </div>
              <Dots rows={2} cols={4} opacity={22} />
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono tracking-wider uppercase text-slate-400 hover:text-crystal-400 border border-transparent hover:border-crystal-400/25 hover:bg-crystal-400/5 transition-all"
            >
              <LogOut className="w-3.5 h-3.5 shrink-0" />
              Abmelden
            </button>

            {/* Version */}
            <div className="flex items-center justify-center gap-1.5 mt-2 pt-2 border-t border-crystal-400/10">
              <span className="font-mono text-[8px] tracking-[0.2em] text-orange-500/60 uppercase">Alpha Version</span>
              <span className="w-px h-2.5 bg-crystal-400/15" />
              <span className="font-mono text-[8px] tracking-[0.15em] text-crystal-400/25 uppercase">Zyklus I</span>
            </div>
          </div>
        </nav>

        {/* ── Main content ─────────────────────────────────────────────────── */}
        <main className="flex-1 overflow-auto pb-16 md:pb-0 min-h-0 flex flex-col">
          <Outlet />
        </main>
      </div>

      <BottomNav />
    </div>
  );
}
